/**
 * Browser RUM for OneUptime.
 *
 * Loaded from `src/instrumentation-client.ts`, which Next runs after the
 * document loads and **before React hydrates**, so the fetch/XHR patches are in
 * place before the page's own requests go out. Everything here is synchronous
 * top-level code for that reason: an `await` or a dynamic `import()` would be
 * fire-and-forget and could resolve after hydration.
 *
 * What it captures:
 *  - document load and every fetch/XHR as spans (failed requests carry an error
 *    status), with `traceparent` propagation to this origin only;
 *  - uncaught errors and unhandled rejections, through the same sanitising
 *    `captureException` the server uses;
 *  - App Router navigations as `route-change` spans, with the route *pattern*;
 *  - Core Web Vitals as `web_vital.*` histograms.
 *
 * Session Replay is deliberately absent: there is no recorder script, no
 * uploader and no DOM capture anywhere in this module.
 *
 * @module lib/observability/browser
 */

import { metrics, trace, type Span } from '@opentelemetry/api'
import { logs } from '@opentelemetry/api-logs'
import { OTLPLogExporter } from '@opentelemetry/exporter-logs-otlp-http'
import { OTLPMetricExporter } from '@opentelemetry/exporter-metrics-otlp-http'
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http'
import { registerInstrumentations } from '@opentelemetry/instrumentation'
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load'
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch'
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request'
import { browserDetector } from '@opentelemetry/opentelemetry-browser-detector'
import { detectResources, resourceFromAttributes } from '@opentelemetry/resources'
import { BatchLogRecordProcessor, LoggerProvider } from '@opentelemetry/sdk-logs'
import { MeterProvider, PeriodicExportingMetricReader } from '@opentelemetry/sdk-metrics'
import { BatchSpanProcessor } from '@opentelemetry/sdk-trace-base'
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web'
import { onCLS, onFCP, onINP, onLCP, onTTFB, type Metric } from 'web-vitals'

import { captureException } from './errors'
import { deploymentEnvironment, releaseVersion } from './identity'
import {
  ROUTE_CHANGE_SPAN_NAME,
  RUM_SERVICE_NAME,
  otlpEndpoint,
  oneUptimeHeaders,
  serviceResourceAttributes,
} from './otlp'
import { RedactingLogRecordProcessor, RedactingSpanProcessor } from './redact'
import { toRoutePattern } from './routes'
import { createSampler, parseTraceSampleRatio } from './sampling'

/**
 * Exact four approved production HTTPS origins (PMS 202).
 * Non-default ports and unencrypted HTTP variants are strictly rejected.
 */
export const APPROVED_ORIGINS: ReadonlySet<string> = new Set([
  'https://crafthomeremodel.com',
  'https://www.crafthomeremodel.com',
  'https://crafthomesandiego.com',
  'https://www.crafthomesandiego.com',
])

const LOOPBACK_HOSTS: ReadonlySet<string> = new Set(['localhost', '127.0.0.1', '[::1]', '::1'])

/**
 * Returns true if the normalized URL's hostname is a local loopback interface.
 */
export function isLoopbackOrigin(originOrUrl: string): boolean {
  try {
    const url = new URL(originOrUrl)
    return LOOPBACK_HOSTS.has(url.hostname.toLowerCase())
  } catch {
    return false
  }
}

/**
 * Normalizes an origin or URL string to lowercase origin without trailing slash.
 */
export function normalizeOrigin(raw: string): string | undefined {
  try {
    return new URL(raw).origin.toLowerCase()
  } catch {
    return undefined
  }
}

/**
 * True when telemetry may run on this page's origin.
 * In production deployments (safe inlined Next.js predicate NEXT_PUBLIC_DEPLOY_ENV === 'production'),
 * only the 4 approved HTTPS origins are permitted.
 * In development / local testing, loopback is permitted, but HTTP and nondefault-port variants
 * of customer domains are strictly rejected.
 */
export function isTelemetryOriginAllowed(originOrUrl: string): boolean {
  const normalized = normalizeOrigin(originOrUrl)
  if (!normalized) return false

  // Exact 4 approved HTTPS origins are always allowed
  if (APPROVED_ORIGINS.has(normalized)) {
    return true
  }

  // Safe Next.js inlined predicate: if explicitly compiled for production deployment,
  // loopback is disabled so dev traffic cannot leak into production RUM.
  const isProductionDeploy = process.env.NEXT_PUBLIC_DEPLOY_ENV === 'production'
  if (isProductionDeploy) {
    return false
  }

  // Dev loopback is allowed for local development and test harnesses
  return isLoopbackOrigin(normalized)
}

/** Backward-compatible helper checking an origin or hostname */
export function isTelemetryHostAllowed(originOrHost: string): boolean {
  if (originOrHost.includes('://')) {
    return isTelemetryOriginAllowed(originOrHost)
  }
  return isTelemetryOriginAllowed(`https://${originOrHost}`)
}

export interface BrowserTelemetryConfig {
  readonly url: string
  readonly token: string
  readonly ratio: number
}

let started = false

/** Read the build-inlined public configuration. Absent values disable RUM. */
export function readBrowserConfig(): BrowserTelemetryConfig | undefined {
  try {
    const url = process.env.NEXT_PUBLIC_ONEUPTIME_URL?.trim() ?? ''
    const token = process.env.NEXT_PUBLIC_ONEUPTIME_RUM_KEY?.trim() ?? ''
    if (url === '' || token === '') return undefined
    return {
      url,
      token,
      ratio: parseTraceSampleRatio(process.env.NEXT_PUBLIC_ONEUPTIME_TRACE_SAMPLE_RATIO),
    }
  } catch {
    return undefined
  }
}

/**
 * Coarse browser attributes. `browserDetector` reads UA Client Hints, which
 * only Chromium has; Safari and Firefox still need `browser.language` (that
 * alone files the batch as RUM) and a coarse `browser.platform` so the Clients
 * page has a row at all. Every value here is a low-cardinality constant.
 */
function browserAttributes(): Record<string, string | boolean> {
  const attributes: Record<string, string | boolean> = {
    'browser.language': 'unknown',
    'browser.platform': 'unknown',
    'browser.mobile': false,
  }
  try {
    if (typeof navigator !== 'undefined') {
      attributes['browser.language'] = navigator.language || 'unknown'
      attributes['user_agent.original'] = navigator.userAgent
      const hints = (
        navigator as Navigator & {
          userAgentData?: { platform?: string; mobile?: boolean }
        }
      ).userAgentData
      if (hints?.platform) attributes['browser.platform'] = hints.platform
      if (typeof hints?.mobile === 'boolean') attributes['browser.mobile'] = hints.mobile
    }
  } catch {
    // Keep the defaults; a locked-down navigator must not break the bootstrap.
  }
  return attributes
}

/** Current route pattern for the page, used to label navigations and vitals. */
function currentRoute(): string {
  try {
    return toRoutePattern(window.location.href)
  } catch {
    return '/'
  }
}

let openRouteSpan: Span | undefined
/** Last route start, so Next's hook and the history patch cannot double-count. */
let lastRoute: { url: string; at: number } | undefined

/**
 * Start a navigation span and end the previous one. Called from Next's
 * `onRouterTransitionStart` hook (and from `popstate` for safety), so an App
 * Router navigation appears as a `route-change` span with the route pattern —
 * never the concrete URL.
 */
export function startRouteChange(url: string, navigationType: string = 'push'): void {
  try {
    const now = Date.now()
    if (lastRoute && lastRoute.url === url && now - lastRoute.at < 250) return
    lastRoute = { url, at: now }
    if (openRouteSpan) {
      openRouteSpan.end()
      openRouteSpan = undefined
    }
    const tracer = trace.getTracer('app-router')
    const route = toRoutePattern(url)
    const span = tracer.startSpan(ROUTE_CHANGE_SPAN_NAME, {
      attributes: { 'app.route': route, 'app.navigation.type': navigationType },
    })
    openRouteSpan = span
    const end = (): void => {
      if (openRouteSpan === span) {
        span.end()
        openRouteSpan = undefined
      }
    }
    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(end)
    else setTimeout(end, 0)
  } catch {
    // A navigation must never be blocked by telemetry.
  }
}

/**
 * App Router navigations reach this module two ways: Next's
 * `onRouterTransitionStart` hook (exported from instrumentation-client.ts) and
 * the History API. The hook covers `<Link>` and `router.push`; the patch covers
 * the same navigations when the hook is unavailable and the browser's back /
 * forward buttons, which Next reports as `traverse`. `startRouteChange`
 * de-duplicates the overlap.
 */
function installNavigationObservers(): void {
  try {
    const patch = (method: 'pushState' | 'replaceState'): void => {
      const original = history[method]
      if (typeof original !== 'function') return
      const wrapped = function patchedHistory(this: History, ...args: unknown[]): unknown {
        const result = Reflect.apply(original, this, args)
        try {
          const url = typeof args[2] === 'string' ? args[2] : window.location.href
          startRouteChange(
            new URL(url, window.location.href).href,
            method === 'pushState' ? 'push' : 'replace',
          )
        } catch {
          // A navigation must never fail because of telemetry.
        }
        return result
      }
      history[method] = wrapped
    }
    patch('pushState')
    patch('replaceState')
    window.addEventListener('popstate', () => {
      startRouteChange(window.location.href, 'traverse')
    })
  } catch {
    // Older or locked-down browsers simply report fewer navigations.
  }
}

/** Histograms for the five Core Web Vitals, bucketed on Google's limits. */
function registerWebVitals(): void {
  try {
    const meter = metrics.getMeter('web-vitals')
    const buckets = (...edges: number[]): { advice: { explicitBucketBoundaries: number[] } } => ({
      advice: { explicitBucketBoundaries: edges },
    })
    const lcp = meter.createHistogram('web_vital.lcp', {
      unit: 'ms',
      ...buckets(0, 1000, 1500, 2000, 2500, 3000, 3500, 4000, 5000, 7500, 10000),
    })
    const inp = meter.createHistogram('web_vital.inp', {
      unit: 'ms',
      ...buckets(0, 50, 100, 150, 200, 250, 300, 400, 500, 750, 1000, 2000),
    })
    const fcp = meter.createHistogram('web_vital.fcp', {
      unit: 'ms',
      ...buckets(0, 600, 1200, 1800, 2400, 3000, 4000, 6000, 10000),
    })
    const ttfb = meter.createHistogram('web_vital.ttfb', {
      unit: 'ms',
      ...buckets(0, 200, 400, 600, 800, 1200, 1800, 2500, 4000),
    })
    const cls = meter.createHistogram('web_vital.cls', {
      unit: '1',
      ...buckets(0, 0.025, 0.05, 0.1, 0.15, 0.2, 0.25, 0.4, 0.6, 1),
    })

    const record =
      (histogram: { record: (value: number, attributes?: Record<string, string>) => void }) =>
      (metric: Metric): void => {
        try {
          histogram.record(metric.value, {
            'web_vital.rating': metric.rating,
            'app.route': currentRoute(),
          })
        } catch {
          // A metric must never break the page.
        }
      }

    onLCP(record(lcp))
    onINP(record(inp))
    onCLS(record(cls))
    onFCP(record(fcp))
    onTTFB(record(ttfb))
  } catch {
    // Vital collection is optional; traces and errors are not.
  }
}

/**
 * Start browser telemetry. Returns `false` when the public URL or Browser key
 * is not configured, in which case nothing is patched and nothing is sent.
 */
export function initBrowserTelemetry(): boolean {
  if (started) return true
  try {
    if (typeof window !== 'undefined' && !isTelemetryOriginAllowed(window.location.origin)) {
      return false
    }
  } catch {
    return false
  }
  const settings = readBrowserConfig()
  if (!settings) return false
  started = true

  try {
    const headers = oneUptimeHeaders(settings.token)
    const release = releaseVersion()
    const resource = resourceFromAttributes({
      ...serviceResourceAttributes(RUM_SERVICE_NAME, release, deploymentEnvironment()),
      ...browserAttributes(),
    }).merge(detectResources({ detectors: [browserDetector] }))

    const tracerProvider = new WebTracerProvider({
      resource,
      sampler: createSampler(settings.ratio),
      spanProcessors: [
        new RedactingSpanProcessor(
          new BatchSpanProcessor(
            new OTLPTraceExporter({ url: otlpEndpoint(settings.url, 'traces'), headers }),
          ),
        ),
      ],
    })
    /**
     * No `ZoneContextManager`. zone.js patches `Promise` and turns an unhandled
     * rejection into a zone error instead of letting the browser raise the
     * native `unhandledrejection` event — measured here, with the page's own
     * listener never firing. Unhandled rejections are one of the signals this
     * app must report, so the default (stack) context manager is used and async
     * context is not propagated across promise boundaries. Errors are recorded
     * regardless of a parent context.
     */
    tracerProvider.register()

    logs.setGlobalLoggerProvider(
      new LoggerProvider({
        resource,
        processors: [
          new RedactingLogRecordProcessor(
            new BatchLogRecordProcessor({
              exporter: new OTLPLogExporter({ url: otlpEndpoint(settings.url, 'logs'), headers }),
            }),
          ),
        ],
      }),
    )

    const meterProvider = new MeterProvider({
      resource,
      readers: [
        new PeriodicExportingMetricReader({
          exporter: new OTLPMetricExporter({ url: otlpEndpoint(settings.url, 'metrics'), headers }),
          exportIntervalMillis: 30000,
        }),
      ],
    })
    metrics.setGlobalMeterProvider(meterProvider)

    const otlpOrigin = settings.url.replace(/\/+$/, '')
    registerInstrumentations({
      instrumentations: [
        new DocumentLoadInstrumentation(),
        new FetchInstrumentation({
          ignoreUrls: [new RegExp(`^${otlpOrigin.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}/`)],
          propagateTraceHeaderCorsUrls: [window.location.origin],
        }),
        new XMLHttpRequestInstrumentation({
          ignoreUrls: [new RegExp(`^${otlpOrigin.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}/`)],
          propagateTraceHeaderCorsUrls: [window.location.origin],
        }),
      ],
    })

    window.addEventListener('error', (event: ErrorEvent) => {
      captureException(event.error ?? event.message)
    })
    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
      captureException(event.reason)
    })

    // Vitals are reported when the page is hidden, so flush at that moment.
    const flush = (): void => {
      if (document.visibilityState === 'hidden') {
        void meterProvider.forceFlush().catch(() => undefined)
        void tracerProvider.forceFlush().catch(() => undefined)
      }
    }
    document.addEventListener('visibilitychange', flush)
    window.addEventListener('pagehide', flush)

    installNavigationObservers()
    registerWebVitals()
    return true
  } catch {
    // A telemetry failure must not stop the site from working.
    return false
  }
}
